import {
  ADD_CUSTOM_SPACING,
  ADD_NEW_FORM,
  DELETE_FORM,
  IGNORE_PUMPING_SERVICE_COMMAND,
  LOADED_FOR_PUMP_FILTER,
  LOAD_CATALOGS_COMPLETE,
  LOAD_CONFIG_COMPLETE,
  LOAD_PRODUCTS_SELECTION,
  LOAD_PRODUCTS_SELECTION_COMPLETE,
  LOAD_PRODUCTS_UL_SELECTION,
  LOAD_PRODUCTS_UL_SELECTION_COMPLETE,
  LOAD_PROJECT_PROFILES_COMPLETE,
  POPULATE_PLANTS_FROM_OPEN_CATALOG,
  POPULATE_PLANT_FROM_OPEN_CATALOG,
  POPULATE_PRODUCT_FROM_OPEN_CATALOG,
  PUMP_DISPLAY_WARNING_MSG,
  RESET,
  RESET_FORM_VALUES,
  RESET_PLANTS,
  SELECT_PRODUCT,
  SET_CAN_ADD_MULTIPLE_PRODUCTS,
  SET_FORM_VALUES,
  SpecificationActions,
} from '../../actions/specification.actions';
import { CemexPumpActions, LOAD_CONFIG_PUMP_COMPLETE, REMOVE_PUMPING_FORMS } from '../../actions/cemex-pump.actions';

import { Catalog } from '@cemex-core/cmx-api/models/catalogs/catalog';
import { FieldConfig } from '../../../../../shared/modules/configurable-form/models/field-configs/field-config.interface';
import { IProduct } from '@cemex-core/cmx-api/models/product/models/product.interface';
import { Plant } from '@cemex-core/cmx-api/models/order-management/plant';
import { Profile } from '@cemex-core/cmx-api/models/profiles-management/profile';
import { SpecificationFormEnum } from '../../models/specification-form.enum';
import { SpecificationFormValues } from '../../models/specification-form-values';
import { TimeWindow } from '@cemex-core/cmx-api/models/time-windows';
import { TimeWindowsActions } from '../../actions/time-windows.actions';
import { Vehicle } from '@cemex-core/cmx-api/models/vehicles/model/vehicle';
import { createSelector } from '@ngrx/store';
import { uniqueByKeys } from '../../../../../shared/utils/unique-by-keys';
import { CATALOG_CODE } from '@ordersnproducts/modules/shared/constants/catalog-codes';

/**
 * Configuration and state for the form in the product-specification step of the
 * customer-order workflow.
 */
export interface SpecificationFormState {
  /**
   * Form-field set-up, loaded from a configuration file.
   */
  config: FieldConfig[];
  /**
   * Form-field set-up for cement pumps.
   */
  configPump: FieldConfig[];
  catalogs: Catalog[];
  productsRaw: IProduct[];
  productSelectionProducts: IProduct[];
  projectProfiles: Profile[];
  deliveryTimeWindows: TimeWindow[];
  forms: SpecificationFormValues[];
  canAddMultipleProducts: boolean;
  canAddMultipleCemexPump: boolean;
  usesMasterContract: boolean;
  modalNoProducts: boolean;
  plants: Plant[];
  vehicles: Vehicle[];
  formsCounter: number;
  ignorePumpingServiceCommand: boolean;
  newOrderCreation?: boolean;
  /**
   * UI state: Client is currently pre-loading products
   */
  nowPreloadingProducts: boolean;
  ignorePumpingServiceFromSelection: boolean;
  displayPumpingInformationDialog: boolean;
  topProducts: IProduct[];
}

/**
 * A default, empty, initial state for the specification-form.
 * @type SpecificationFormState
 */
export const initialState: SpecificationFormState = {
  config: [],
  configPump: [],
  catalogs: [],
  productsRaw: [],
  productSelectionProducts: [],
  projectProfiles: [],
  deliveryTimeWindows: [],
  forms: [{ id: 0, type: SpecificationFormEnum.PRODUCT_FORM, values: {} }],
  canAddMultipleProducts: false,
  canAddMultipleCemexPump: false,
  usesMasterContract: false,
  modalNoProducts: false,
  plants: [],
  vehicles: [],
  formsCounter: 0,
  ignorePumpingServiceCommand: false,
  nowPreloadingProducts: false,
  ignorePumpingServiceFromSelection: false,
  displayPumpingInformationDialog: false,
  topProducts: [],
  newOrderCreation: false,
};

export const uniqueProductKeys = ['commercialCode', 'commercialDesc', 'productCode', 'productDesc','commercialMixRef'];
export const uniqueProductKeysOP = ['productCode', 'productDesc'];
export const uniquePlantKeys = ['plantDesc'];

export const getConfig = (state: SpecificationFormState) => state.config;
export const getConfigPump = (state: SpecificationFormState) => state.configPump;
export const getProjectProfiles = (state: SpecificationFormState) => state.projectProfiles;
export const getDeliveryTimeWindows = (state: SpecificationFormState) => state.deliveryTimeWindows;
export const getProducts = (state: SpecificationFormState) => state.productSelectionProducts;
export const getPlants = (state: SpecificationFormState) => state.plants;
export const getVehicles = (state: SpecificationFormState) => state.vehicles;
export const getPumpMessage = (state: SpecificationFormState) => state.displayPumpingInformationDialog;

export const getIgnorePumpingServiceCommand = (state: SpecificationFormState) => state.ignorePumpingServiceCommand;

export const ignorePumpingServiceFromSelection = (state: SpecificationFormState) =>
  state.ignorePumpingServiceFromSelection;

export function filterUniqueProducts(products) {
  return uniqueByKeys(products, uniqueProductKeys);
}
export function filterUniqueProductsOP(products) {
  return uniqueByKeys(products, uniqueProductKeysOP);
}

export function filterUniquePlants(plants) {
  return uniqueByKeys(plants, uniquePlantKeys);
}

/**
 * Updates the specification-form state based on the type and payload of action received.  Not all
 * action-types are explicitly handled here:  Some are left to fall into the default.
 * @param {SpecificationFormState} state The portion of the store that holds this form's state.
 * @param {SpecificationActions | TimeWindowsActions | CemexPumpActions} action
 * @return {SpecificationFormState}
 */
export function specificationFormReducer(
  state: SpecificationFormState = initialState,
  action: SpecificationActions | TimeWindowsActions | CemexPumpActions,
): SpecificationFormState {
  switch (action.type) {
    case LOAD_CATALOGS_COMPLETE:
      return {
        ...state,
        catalogs: action.payload,
      } as SpecificationFormState;

    case LOAD_CONFIG_COMPLETE:
      const canAddMultipleProducts = !!action.payload['canAddMultipleProducts'];
      const canAddMultipleCemexPump = !!action.payload['canAddMultipleCemexPump'];
      const usesMasterContract = !!action.payload['usesMasterContract'];
      const modalNoProducts = !!action.payload['modalNoProducts'];
      const newOrderCreation = !!action.payload['newOrderCreation'];

      return {
        ...state,
        config: action.payload.fields,
        canAddMultipleProducts: canAddMultipleProducts,
        canAddMultipleCemexPump: canAddMultipleCemexPump,
        usesMasterContract: usesMasterContract,
        modalNoProducts: modalNoProducts,
        newOrderCreation: newOrderCreation,
      } as SpecificationFormState;

    case LOAD_CONFIG_PUMP_COMPLETE:
      return {
        ...state,
        configPump: action.payload.cemexPump,
      } as SpecificationFormState;

    case LOAD_PRODUCTS_UL_SELECTION:
    case LOAD_PRODUCTS_SELECTION:
      // When loading products, turn on the loading-spinner for that form-field.
      return {
        ...state,
        nowPreloadingProducts: true,
      } as SpecificationFormState;

    case LOAD_PRODUCTS_SELECTION_COMPLETE:
      return {
        ...state,
        productsRaw: [...state.productsRaw, ...action.payload],
        // Add just distinct products
        productSelectionProducts: filterUniqueProducts([...state.productSelectionProducts, ...action.payload]),
        nowPreloadingProducts: false,
        topProducts: filterUniqueProducts(action.payload),
      } as SpecificationFormState;

    case LOAD_PRODUCTS_UL_SELECTION_COMPLETE:
      return {
        ...state,
        productsRaw: filterUniqueProductsOP([...state.productsRaw, ...action.payload]),
        productSelectionProducts: filterUniqueProductsOP([...state.productSelectionProducts, ...action.payload]),
        nowPreloadingProducts: false,
        topProductsOP: filterUniqueProductsOP(action.payload),
      } as SpecificationFormState;

    case POPULATE_PRODUCT_FROM_OPEN_CATALOG:
      return {
        ...state,
        productsRaw: [...state.productsRaw, action.payload],
        productSelectionProducts: filterUniqueProducts([...state.productSelectionProducts, action.payload]),
      } as SpecificationFormState;

    case POPULATE_PLANT_FROM_OPEN_CATALOG:
      return {
        ...state,
        plants: filterUniquePlants([...state.plants, action.payload]),
      } as SpecificationFormState;

    case POPULATE_PLANTS_FROM_OPEN_CATALOG:
      return {
        ...state,
        plants: filterUniquePlants([...state.plants, ...action.payload]),
      } as SpecificationFormState;

    case LOAD_PROJECT_PROFILES_COMPLETE:
      return {
        ...state,
        projectProfiles: action.payload,
      } as SpecificationFormState;

    case ADD_NEW_FORM:
      const counter = state.formsCounter + 1;
      return {
        ...state,
        forms: [
          {
            id: counter,
            type: action.payload.formType,
            values: {},
            ...(!isNaN(action.payload.relatedProductId) && { relatedProductId: action.payload.relatedProductId }),
          },
          ...state.forms,
        ],
        formsCounter: counter,
      } as SpecificationFormState;

    case DELETE_FORM:
      return {
        ...state,
        forms: [...state.forms.filter((form) => form.id !== action.payload)],
      } as SpecificationFormState;

    case SET_FORM_VALUES:
      return {
        ...state,
        forms: action.payload,
      } as SpecificationFormState;

    case RESET_FORM_VALUES:
      return {
        ...state,
        forms: [],
        formsCounter: 0,
      } as SpecificationFormState;

    case REMOVE_PUMPING_FORMS:
      return {
        ...state,
        forms: state.forms.filter(
          (forms) => forms.type === SpecificationFormEnum.PRODUCT_FORM || forms.relatedProductId !== action.payload,
        ),
      } as SpecificationFormState;
    case SELECT_PRODUCT:
      return {
        ...state,
        productsRaw: [action.payload, ...state.productsRaw],
        productSelectionProducts: filterUniqueProducts([action.payload, ...state.productSelectionProducts]),
      } as SpecificationFormState;

    case SET_CAN_ADD_MULTIPLE_PRODUCTS:
      return {
        ...state,
        canAddMultipleProducts: action.payload,
      };

    case IGNORE_PUMPING_SERVICE_COMMAND:
      return {
        ...state,
        ignorePumpingServiceCommand: action.payload,
      };

    case RESET:
      return {
        ...initialState,
      };

    case RESET_PLANTS:
      return {
        ...state,
        plants: [],
      };
    case LOADED_FOR_PUMP_FILTER:
      return {
        ...state,
        ignorePumpingServiceFromSelection: action.payload,
      };

    case PUMP_DISPLAY_WARNING_MSG:
      return {
        ...state,
        displayPumpingInformationDialog: action.payload,
      };

    // If custom spacing exists in the order, then add it to the spacing catalog and place it in the right place
    case ADD_CUSTOM_SPACING:
      return {
        ...state,
        catalogs: state.catalogs.map((catalog) =>
          catalog.catalogCode === CATALOG_CODE.TIME_PER_LOAD
            ? {
                ...catalog,
                entries: [...catalog.entries, action.payload].sort((a, b) => Number(a.entryCode) - Number(b.entryCode)),
              }
            : catalog,
        ),
      };

    default:
      return state;
  }
}

// todo: triage for removal
export const getSalesDocuments = createSelector(getProducts, (products) => {
  return products.map((product: IProduct) => {
    return product.salesDocument;
  });
});

// todo: triage for removal
export const getSelectedPlants = createSelector(getPlants, (plants) => {
  return plants;
});

export const ignorePumpingServiceFromSelectionSelector = createSelector(ignorePumpingServiceFromSelection, (ignore) => {
  return ignore;
});

// todo: triage for removal
export const getCatalogs = (state: SpecificationFormState) => state.catalogs;
